/**
 * The admin console's modal forms (Add user, Edit org, Onboard, Connect a
 * data source), shared by v1 (components/AdminConsole) and v2
 * (components/AdminV2). Each hook is today's modal logic lifted out of the
 * component unchanged, plus the one fix DECISIONS.md asks for: a failed
 * directory search is reported as an error, never as "no matches".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';
import {
    apiDetailOrMessage, COPY, flattenOrgs, moveOptionsFor, PERSPECTIVE_HINT, ROLE_OPTIONS, slugify,
} from '../utils/adminConsole';
import { DEFAULT_DATABASE, emptySource } from '../components/AdminConsole/DataSourceFields';
import { failedSteps, hasFailingStep, sourceError } from '../components/AdminConsole/DataSourceCheck';

// Directory search starts at this many characters, after this pause.
export const DIRECTORY_MIN_CHARS = 2;
export const DIRECTORY_DEBOUNCE_MS = 300;

/**
 * Debounced Entra directory search. `error` is the server's text (or a
 * fallback) when the search itself failed: the caller must then show it and
 * must not offer to create a new account, since "no match" is unknown.
 */
export function useDirectorySearch(query) {
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const q = query.trim();
        if (q.length < DIRECTORY_MIN_CHARS) { setResults([]); setError(null); setSearching(false); return undefined; }
        let alive = true;
        setSearching(true);
        const t = setTimeout(() => {
            apiService.searchDirectory(q)
                .then((r) => { if (alive) { setResults(r); setError(null); } })
                .catch((e) => {
                    if (!alive) return;
                    setResults([]);
                    const detail = e?.details?.detail;
                    setError(typeof detail === 'string' && detail ? detail : COPY.directoryError);
                })
                .finally(() => { if (alive) setSearching(false); });
        }, DIRECTORY_DEBOUNCE_MS);
        return () => { alive = false; clearTimeout(t); };
    }, [query]);

    const q = query.trim();
    const settled = !searching && q.length >= DIRECTORY_MIN_CHARS;
    return {
        results,
        searching,
        error,
        noMatches: settled && !error && results.length === 0,
        // Today's rule: offered only with no directory hits and an "@" in the query.
        canCreate: settled && !error && results.length === 0 && query.includes('@'),
    };
}

/** Add an existing directory user, or create a new local account (invite). */
export function useAddUserForm({ orgs, me, onAdded, onClose }) {
    const flatOrgs = useMemo(() => flattenOrgs(orgs), [orgs]);
    const vendorOrg = flatOrgs.find((o) => o.type === 'VENDOR');
    const firstDealer = flatOrgs.find((o) => o.type === 'COG');

    const [query, setQuery] = useState('');
    const search = useDirectorySearch(query);
    const [picked, setPicked] = useState(null);
    const [role, setRole] = useState('user');
    const [orgId, setOrgId] = useState(firstDealer?.id || '');
    const [pricepath, setPricepath] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const savingRef = useRef(false);

    // Only the vendor organization grants admin roles; a dealer-admin adds plain users only.
    const roleOptions = me?.is_super_admin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((r) => r.value === 'user');
    const pickedOrg = flatOrgs.find((o) => o.id === orgId);
    const perspective = pickedOrg ? (PERSPECTIVE_HINT[pickedOrg.type] || 'their organization') : null;

    // When switching to super_admin, target the vendor org; otherwise a dealership.
    useEffect(() => {
        if (role === 'super_admin' && vendorOrg) setOrgId(vendorOrg.id);
        else if (role !== 'super_admin' && firstDealer && orgId === vendorOrg?.id) setOrgId(firstDealer.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    const pickNew = () => setPicked({ invite: true, email: query.trim(), display_name: query.trim() });

    const save = () => {
        if (!picked || !orgId || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError(null);
        const common = { org_id: orgId, role, apps: pricepath ? ['pricepath'] : [] };
        const req = picked.invite
            ? apiService.inviteAdminUser({ email: picked.email, display_name: picked.display_name, ...common })
            : apiService.addAdminUser({ entra_object_id: picked.entra_object_id, email: picked.email, display_name: picked.display_name, ...common });
        req
            .then((res) => { onAdded(picked.invite ? res : undefined, { picked, response: res }); onClose(); })
            .catch((e) => setError(apiDetailOrMessage(e, COPY.addUserFailed)))
            .finally(() => { savingRef.current = false; setSaving(false); });
    };

    return {
        flatOrgs, query, setQuery, results: search.results, searching: search.searching, searchError: search.error,
        noMatches: search.noMatches, canCreate: search.canCreate, picked, setPicked, pickNew, role, setRole, roleOptions,
        orgId, setOrgId, pickedOrg, perspective, pricepath, setPricepath, saving, error, save,
    };
}

/**
 * Edit an org: rename, re-slug (warned), suspend / reactivate (super, not the
 * vendor org; cascades), move a dealer (super). Only the changed fields are
 * sent; the move runs after the PATCH.
 */
export function useOrgEditForm({ org, orgs, isSuper, onSaved, onClose }) {
    const [name, setName] = useState(org.name);
    const [slug, setSlugRaw] = useState(org.slug);
    const [status, setStatus] = useState(org.status);
    const [parentId, setParentId] = useState('__keep__'); // sentinel = don't move
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const moveOptions = useMemo(() => moveOptionsFor(org, orgs), [org, orgs]);
    const canMove = isSuper && moveOptions.length > 0;
    const canSuspend = isSuper && org.type !== 'VENDOR';
    const slugChanged = slug !== org.slug;
    const setSlug = (v) => setSlugRaw(slugify(v));

    const save = () => {
        if (saving) return;
        setSaving(true); setError(null);
        const patch = {};
        if (name.trim() !== org.name) patch.name = name.trim();
        if (slugChanged) patch.slug = slug;
        if (canSuspend && status !== org.status) patch.status = status;

        const run = Object.keys(patch).length ? apiService.updateAdminOrg(org.id, patch) : Promise.resolve();
        run
            .then(() => ((canMove && parentId !== '__keep__') ? apiService.moveAdminOrg(org.id, parentId || null) : null))
            .then(() => { onSaved(); onClose(); })
            .catch((e) => setError(apiDetailOrMessage(e, COPY.saveOrgFailed)))
            .finally(() => setSaving(false));
    };

    return {
        name, setName, slug, setSlug, status, setStatus, parentId, setParentId, saving, error,
        moveOptions, canMove, canSuspend, slugChanged, save,
    };
}

export const ONBOARD_STEPS = ['Dealership', 'Structure', 'First admin', 'Data source'];

/**
 * Guided dealer onboarding: create the dealer (+ holding) and invite the
 * first admin in one call, then connect, check and activate its source.
 */
export function useOnboardWizard({ orgs, onClose, onDone }) {
    const holdings = useMemo(() => flattenOrgs(orgs).filter((o) => o.type === 'TOG'), [orgs]);

    const [step, setStep] = useState(0);
    const [dealerName, setDealerName] = useState('');
    const [slugTouched, setSlugTouched] = useState(false);
    const [slug, setSlug] = useState('');
    const [inHolding, setInHolding] = useState(false);
    const [holdingChoice, setHoldingChoice] = useState(''); // existing id | '__new__'
    const [holdingName, setHoldingName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminName, setAdminName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [source, setSource] = useState(emptySource);
    const [created, setCreated] = useState(null); // OnboardResult once the org exists
    const [tenant, setTenant] = useState(null); // the attached source row
    const [check, setCheck] = useState(null); // last connection-check result
    const [busy, setBusy] = useState(null); // 'connect' | 'check' | 'activate'
    const savingRef = useRef(false);

    const effSlug = slug || slugify(dealerName);
    const onName = (v) => { setDealerName(v); if (!slugTouched) setSlug(slugify(v)); };
    const onSlug = (v) => { setSlugTouched(true); setSlug(slugify(v)); };
    const hasEndpoint = source.synapse_server.trim().length > 0;
    const blocked = hasFailingStep(check?.steps);

    const canNext = (step === 0 && dealerName.trim().length > 1)
        || (step === 1 && Boolean(!inHolding || (holdingChoice === '__new__' ? holdingName.trim() : holdingChoice)))
        || step === 2;

    // The dealership exists from the moment onboardDealer returns, so the
    // console is told about it when the wizard closes — however it closes.
    const close = () => { if (created) onDone(created); onClose(); };

    const connect = (orgId, orgSlug) => {
        setBusy('connect'); setError(null);
        return apiService.connectAdminTenant({
            org_id: orgId,
            slug: orgSlug || effSlug,
            synapse_server: source.synapse_server.trim(),
            lake_account: source.lake_account.trim(),
            database: source.database.trim() || DEFAULT_DATABASE,
            is_demo: source.is_demo,
        })
            .then((row) => { setTenant(row); setCheck(null); })
            .catch((e) => setError(sourceError(e, 'The dealership was created, but its data source could not be connected.')))
            .finally(() => setBusy(null));
    };

    // `withSource` false = connect the source later; the onboard call is identical.
    const submit = (withSource) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true); setError(null);
        const payload = {
            dealer_name: dealerName.trim(),
            dealer_slug: effSlug || null,
            holding_id: inHolding && holdingChoice !== '__new__' ? holdingChoice : null,
            holding_name: inHolding && holdingChoice === '__new__' ? holdingName.trim() : null,
            admin_email: adminEmail.trim() || null,
            admin_name: adminName.trim() || null,
        };
        apiService.onboardDealer(payload)
            .then((r) => {
                setCreated(r);
                if (!withSource || !hasEndpoint || !r?.dealer?.id) return null;
                return connect(r.dealer.id, r.dealer.slug);
            })
            .catch((e) => setError(sourceError(e, COPY.onboardFailed)))
            .finally(() => { savingRef.current = false; setSaving(false); });
    };

    // Real database round-trip — seconds, not milliseconds.
    const runCheck = () => {
        setBusy('check'); setError(null);
        apiService.checkAdminTenant(tenant.id)
            .then(setCheck)
            .catch((e) => setError(sourceError(e, 'The connection check could not be completed.')))
            .finally(() => setBusy(null));
    };

    const activate = () => {
        setBusy('activate'); setError(null);
        apiService.activateAdminTenant(tenant.id)
            .then((row) => { setTenant(row); setError(null); })
            .catch((e) => {
                const steps = failedSteps(e);
                if (steps) setCheck({ ok: false, steps });
                setError(sourceError(e, 'This source could not be activated.'));
            })
            .finally(() => setBusy(null));
    };

    return {
        holdings, step, setStep, dealerName, onName, effSlug, onSlug, inHolding, setInHolding, holdingChoice, setHoldingChoice,
        holdingName, setHoldingName, adminEmail, setAdminEmail, adminName, setAdminName, saving, error, source, setSource,
        created, tenant, check, busy, hasEndpoint, blocked, canNext, close, connect, submit, runCheck, activate,
    };
}

/**
 * Data sources (super-admin): the tenant list, plus Check / Activate /
 * Detach with per-row busy state and the per-tenant check results of this
 * session. `enabled` false skips the load (a dealer-admin cannot read them).
 */
export function useTenantOverview({ enabled = true } = {}) {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [checks, setChecks] = useState({}); // tenantId -> check result
    const [confirmId, setConfirmId] = useState(null);
    const [attach, setAttach] = useState(null); // { dealerId } — the modal pre-selects it

    const load = () => apiService.getAdminTenants().then(setTenants).catch(() => {});

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        setLoading(true);
        apiService.getAdminTenants()
            .then((t) => { if (alive) { setTenants(t); setError(null); } })
            .catch(() => { if (alive) setError(COPY.tenantsLoadError); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [enabled]);

    const runCheck = (t) => {
        setBusyId(t.id); setActionError(null);
        return apiService.checkAdminTenant(t.id)
            .then((r) => { setChecks((c) => ({ ...c, [t.id]: r })); return true; })
            .catch((e) => { setActionError(sourceError(e, 'The connection check could not be completed.')); return false; })
            .finally(() => setBusyId(null));
    };

    const activate = (t) => {
        setBusyId(t.id); setActionError(null);
        return apiService.activateAdminTenant(t.id)
            .then(() => load().then(() => true))
            .catch((e) => {
                const steps = failedSteps(e);
                if (steps) setChecks((c) => ({ ...c, [t.id]: { ok: false, steps } }));
                setActionError(sourceError(e, 'This source could not be activated.'));
                return false;
            })
            .finally(() => setBusyId(null));
    };

    const detach = (t) => {
        setBusyId(t.id); setActionError(null);
        return apiService.removeAdminTenant(t.id)
            .then(() => { setConfirmId(null); return load().then(() => true); })
            .catch((e) => { setActionError(sourceError(e, 'Could not detach this source.')); return false; })
            .finally(() => setBusyId(null));
    };

    return {
        tenants, loading, error, actionError, setActionError, busyId, checks, confirmId, setConfirmId,
        attach, setAttach, load, runCheck, activate, detach,
    };
}

/** Connect (or re-point) one dealership's source; one source per dealership. */
export function fieldsForTenant(tenant) {
    if (!tenant) return emptySource();
    return {
        synapse_server: tenant.synapse_server || '',
        lake_account: tenant.lake_account || '',
        database: tenant.database || DEFAULT_DATABASE,
        is_demo: !!tenant.is_demo,
    };
}

export function useAttachSource({ dealers, tenants, dealerId, onSaved, onClose }) {
    const startOrg = dealers.find((d) => d.id === dealerId) || dealers[0] || null;
    const tenantFor = (id) => tenants.find((t) => t.org_id === id) || null;

    const [orgId, setOrgId] = useState(startOrg?.id || '');
    const [slug, setSlugRaw] = useState(tenantFor(startOrg?.id)?.slug || slugify(startOrg?.slug || startOrg?.name || ''));
    const [source, setSource] = useState(() => fieldsForTenant(tenantFor(startOrg?.id)));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const existing = tenantFor(orgId);
    const setSlug = (v) => setSlugRaw(slugify(v));

    // Switching dealership switches which source we are editing — everything follows.
    const pickOrg = (id) => {
        const t = tenantFor(id);
        const d = dealers.find((o) => o.id === id);
        setOrgId(id);
        setSlugRaw(t?.slug || slugify(d?.slug || d?.name || ''));
        setSource(fieldsForTenant(t));
    };

    const save = () => {
        if (saving) return;
        setSaving(true); setError(null);
        apiService.connectAdminTenant({
            org_id: orgId,
            slug: slug.trim(),
            synapse_server: source.synapse_server.trim(),
            lake_account: source.lake_account.trim(),
            database: source.database.trim() || DEFAULT_DATABASE,
            is_demo: source.is_demo,
        })
            .then((row) => { onSaved(row, { existing: Boolean(existing), orgId }); onClose(); })
            .catch((e) => setError(sourceError(e, 'Could not connect this data source.')))
            .finally(() => setSaving(false));
    };

    const canSave = Boolean(orgId && slug.trim() && source.synapse_server.trim());

    return {
        orgId, pickOrg, slug, setSlug, source, setSource, saving, error, existing, tenantFor, canSave, save,
    };
}
